import { useEffect, useState, useCallback, useRef } from 'react';
import { useAppSelector } from '../store/hooks';
import { getTaxLots } from '../api/taxLots';
import type { TaxLotsResponse, CostBasisMethod } from '../api/taxLots';

interface UseTaxLotsResult {
  data: TaxLotsResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useTaxLots(method: CostBasisMethod = 'fifo', symbol?: string): UseTaxLotsResult {
  const userId = useAppSelector((s) => s.auth.user?.id);
  const [data, setData] = useState<TaxLotsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track latest fetch so stale responses from previous methods are ignored
  const fetchIdRef = useRef(0);

  const fetchTaxLots = useCallback(async () => {
    if (!userId) return;
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const result = await getTaxLots(userId, method, symbol);
      if (fetchId === fetchIdRef.current) {
        setData(result);
      }
    } catch (err: unknown) {
      if (fetchId === fetchIdRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load tax lots');
      }
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [userId, method, symbol]);

  // Re-fetch whenever userId, method, or symbol changes
  useEffect(() => {
    fetchTaxLots();
  }, [fetchTaxLots]);

  return { data, isLoading, error, refetch: fetchTaxLots };
}
